import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FormApi, FormState } from 'final-form';

import Leasehold from 'src/models/leasehold/Leasehold';
import OwnershipContactCategory from 'src/models/OwnershipContactCategory';
import Ownership from 'src/models/tract_detail/Ownership';

import OwnershipCard from './OwnershipCard';
import _ from 'lodash';
import { SelectItem } from 'src/models/common/SelectItem';
import Button from 'src/components/controls/forms/redesign/FormButton';
import DialogList from 'src/dialogs';
import LeaseholdPicker from './LeaseholdPicker';
import { AnyObject, FormSpy } from 'react-final-form';
import PropertyUtil from 'src/util/PropertyUtil';
import PagedResultSet from 'src/models/common/PagedResultSet';
import MathUtil from 'src/util/MathUtil';
import ProjectStaff from 'src/models/ProjectStaff';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import { DialogContext, dialogCreators } from 'src/providers/DialogProvider';
import PagedItemList from 'src/components/common/PagedItemList';
import { ITableConfig } from 'src/components/common/table';
import { ODataFilter, ODataFilterOperators, ODataOptions, ODataOrderByDirection } from 'src/util/ODataBuilder';
import useFetch from 'use-http';
import { IAppContextState } from 'src/reducers/AppContext';
import { AppContext } from 'src/providers/AppContextProvider';
import LoadingSpinner from 'src/components/common/LoadingSpinner';
import Project from 'src/models/Project';
import OwnershipCategory from 'src/models/OwnershipCategory';

interface IOwnershipListStateProps {
    clientNumber: number;
    projectNumber: number;
    project: Project;
    fieldNumber: number;
    tractNumber: number;
    horizonNumber: number;
    horizon: number;
    form: FormApi;
    leaseholds: Array<Leasehold>;
    ownershipContactCategories: Array<OwnershipContactCategory>;
    totalInterest: number;
    executiveRights: number;
    grossAcres: number;
    projectStaff: PagedResultSet<ProjectStaff>;
    errorMessage: string;
    hasNonExecutiveAdditionalOwnership: boolean;
    idVerificationMessage: string;
    idVerificationResult: boolean;

    navigate: (path: string) => void;
    showOwnerSearch: (dialogName: string) => void;
    setDialogShowing: (dialogName: string) => void;
    getLeaseholds: (ownershipId: number) => void;
    ratifyAdditionalOwner: (ownershipId: number, parentOwnershipId: number) => void;
    getLeaseholdsByTract: (clientNumber: number, projectNumber: number, sectionNumber: number, tractNumber: number) => void;
    getLeaseholdsForDelete: (ownershipId: number) => void;
    hideDialog: () => void;
    addAdditionalOwnership: (parentOwnershipId: number) => void;
    deleteAdditionalOwnership: (additionalOwnershipId: number) => void;
    openOwnershipNotes: (ownershipId: number) => void;
    deleteOwnership: (ownershipId: number) => void;
    promoteAdditionalOwnership: (ownershipId: number) => void;
    clearError: () => void;
    initOwnerTracts: () => void;
    clearVerification: () => void;
    setVerificationMessage: (message: string) => void;
    cloneData: (context: string, options: string) => void;
    navigateAcquisition: (ownershipId: number, ownerLookupId: number) => void;
    openContactNotes: (ownership: Ownership) => void;
    onAddOwnership: () => void;
    resetForm: (ownerships: PagedResultSet<Ownership>) => void;
}

const OwnershipList = (props: IOwnershipListStateProps) => {
    const initConfig: ITableConfig = {
        rowCount: 25,
        pageNumber: 1,
    };

    const dialogContext = React.useContext<dialogCreators>(DialogContext);
    const { get, response } = useFetch();
    const appContext = useContext<IAppContextState>(AppContext);

    const [selectedOwnershipId, setSelectedOwnershipId] = useState(undefined);
    const [ownerships, setOwnerships] = useState<PagedResultSet<Ownership>>(undefined);
    const [interestStatus, setInterestStatus] = useState('good');
    const [rightsStatus, setRightsStatus] = useState('good');
    const [totalInterest, setTotalInterest] = useState(props.totalInterest);
    const [totalAcreage, setTotalAcreage] = useState('0');
    const [initialInterests, setInitialInterests] = useState([]);
    const [initialExecutiveRights, setInitialExecutiveRights] = useState([]);
    const [deltaInterests, setDeltaInterests] = useState([]);
    const [deltaExecutiveRights, setDeltaExecutiveRights] = useState([]);
    const [showLeaseButton, setShowLeaseButton] = useState<boolean>(true);
    const [showCategoryButton, setShowCategoryButton] = useState<boolean>(true);
    const [ownershipConfig, setOwnershipConfig] = useState<ITableConfig>(initConfig);
    const [loadingItems, setLoadingItems] = useState<boolean>(false);
    const [searchText, setSearchText] = useState<string>('');
    // Setting to non-form state to prevent render lag and race conditions
    const [pageList, setPageList] = useState<PagedResultSet<Ownership>>(undefined);

    // Mirror the interest/rights state into refs so the focus/change handlers
    // below can be wrapped in useCallback with a stable identity (so memoized
    // OwnershipCards don't re-render on every keystroke) while still reading the
    // latest values.
    const initialInterestsRef = useRef(initialInterests);
    const initialExecutiveRightsRef = useRef(initialExecutiveRights);
    const deltaInterestsRef = useRef(deltaInterests);
    const deltaExecutiveRightsRef = useRef(deltaExecutiveRights);
    initialInterestsRef.current = initialInterests;
    initialExecutiveRightsRef.current = initialExecutiveRights;
    deltaInterestsRef.current = deltaInterests;
    deltaExecutiveRightsRef.current = deltaExecutiveRights;

    useEffect(() => {
        if (!!pageList && !!props.project) {
            let formatted: PagedResultSet<Ownership> = {
                ...pageList,
                items: prepOwnerships(pageList.items)
            };

            setOwnerships(formatted);
        }
    }, [pageList, props.project]);

    useEffect(() => {
        setLoadingItems(false);
        if (!!ownerships) {
            handleRegisterOwnerships();
            if (ownerships?.items?.length > 0) {
                calcTotalInterest();
                calcTotalAcres();
            }
        }
    }, [ownerships]);

    useEffect(() => {
        if (!totalInterest || totalInterest === 0) {
            calcTotalInterest();
        }
        if (!!props.grossAcres) {
            calcTotalAcres();
        }
    }, [props.grossAcres, totalInterest]);
    
    useEffect(() => {
        if (deltaInterests?.length > 0) {
            calcTotalInterest();
            calcTotalAcres();
        }
    }, [deltaInterests]);

    useEffect(() => {
        if (ownershipConfig !== undefined) {
            setLoadingItems(true);
        }
    }, [ownershipConfig]);

    useEffect(() => {
        if (loadingItems === true) {
            getOwnerships();
        } else if (ownerships?.pageNumber === 1) {
            let element = document.getElementById('ownership-list-scroll');
            if (element !== null) {
                element.scrollTop = 0;
            }
        }
    }, [loadingItems]);

    // tslint:disable-next-line:no-any
    const isNumber = (testSubject: any) => {
        return !isNaN(parseFloat(testSubject)) && isFinite(testSubject);
    };

    const prepOwnerships = (curOwnerships: Array<Ownership>) => {
        for (let o of curOwnerships) {
            let categories = o.ownershipCategories ?? [];

            let oids = categories.map(oc => oc.categoryTypeId);
            let missing = props.project?.ownershipCategoryTypes
                ?.filter(oct => !oids.includes(oct.id))
                ?.map(oct => {
                    let newCat: OwnershipCategory = {
                        categoryTypeId: oct.id,
                        categoryTypeName: oct.name,
                        ownershipId: o.id
                    };

                    return newCat;
                }
            );

            if (missing?.length > 0) {
                categories = categories.concat(missing);
            }

            categories.sort((a, b) => {
                return a.categoryTypeName?.localeCompare(b.categoryTypeName) ?? 0;
            });
            
            o.ownershipCategories = categories;

            if (o.additionalOwnerships?.length > 0) {
                o.additionalOwnerships = prepOwnerships(o.additionalOwnerships);
            }
        }

        return curOwnerships;
    };

    const onFormChange = (fs: FormState<AnyObject>, form: FormApi) => {
        const regex = /[0-9]/g;

        if (fs.active) {
            const recordIndex = PropertyUtil.SafeGet(() => fs.active.match(regex)[0]);

            if (fs.active.includes('decimalInterest') && PropertyUtil.SafeGet(() => isNumber(fs?.values?.ownerships[recordIndex]?.decimalInterest))) {
                if (fs.active.includes('additionalOwnerships')) {
                    const additionalOwnershipIndex = PropertyUtil.SafeGet(() => fs.active.match(regex)[1]);
                    // tslint:disable-next-line: max-line-length
                    form.change(
                        `ownerships[${recordIndex}].additionalOwnerships[${additionalOwnershipIndex}].netAcres`,
                        MathUtil.cleanNumber(
                            fs.values.ownerships[recordIndex].additionalOwnerships[additionalOwnershipIndex].decimalInterest * props.grossAcres
                        )
                    );
                } else {
                    form.change(
                        `ownerships[${recordIndex}].netAcres`,
                        MathUtil.cleanNumber(fs.values.ownerships[recordIndex].decimalInterest * props.grossAcres)
                    );
                }
            } else if (fs.active.includes('netAcres') && isNumber(fs.values.ownerships[recordIndex].netAcres)) {
                if (fs.active.includes('additionalOwnerships')) {
                    const additionalOwnershipIndex = PropertyUtil.SafeGet(() => fs.active.match(regex)[1]);
                    // tslint:disable-next-line: max-line-length
                    form.change(
                        `ownerships[${recordIndex}].additionalOwnerships[${additionalOwnershipIndex}].decimalInterest`,
                        MathUtil.cleanNumber(fs.values.ownerships[recordIndex].additionalOwnerships[additionalOwnershipIndex].netAcres / props.grossAcres)
                    );
                } else {
                    form.change(
                        `ownerships[${recordIndex}].decimalInterest`,
                        MathUtil.cleanNumber(fs.values.ownerships[recordIndex].netAcres / props.grossAcres)
                    );
                }
            }
        }
    };

    const getOwnerships = async () => {
        const options = ODataOptions.from(ownershipConfig);
        if (searchText?.length > 0) {
            options.addFilter(new ODataFilter('omni', ODataFilterOperators.Contains, searchText));
        }

        // tslint:disable-next-line:max-line-length
        let result = await get(`v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/fields/${props.fieldNumber}/tracts/${appContext.tractNumber}/horizons/${appContext.horizonNumber}/paged-ownerships/${options.toString()}`);

        if (response.ok) {
            let newList = result;
            if (result.pageNumber > 1) {
                newList.items = ownerships.items.concat(newList.items);
            }

            setPageList(newList);
        } else {
            console.log('Unable to get ownerships!');
        }
    };

    const onNavigateLeaseInfo = useCallback((ownershipId: number) => {
        setSelectedOwnershipId(ownershipId);
        props.getLeaseholds(ownershipId);
    }, [props.getLeaseholds]);

    const onRatifyAdditionalOwner = useCallback((ownershipId: number, parentOwnershipId: number) => {
        setSelectedOwnershipId(ownershipId);
        props.ratifyAdditionalOwner(ownershipId, parentOwnershipId);
    }, [props.ratifyAdditionalOwner]);

    const onDeleteOwnership = useCallback((ownershipId: number) => {
        setSelectedOwnershipId(ownershipId);
        props.getLeaseholdsForDelete(ownershipId);
    }, [props.getLeaseholdsForDelete]);

    const onMakeMineralOwnership = useCallback((ownershipId: number) => {
        props.promoteAdditionalOwnership(ownershipId);
    }, [props.promoteAdditionalOwnership]);

    const addInitialInterestItem = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
        const activeField = props.form.getState().active;

        const regexDecimalInterest = /ownerships\[[0-9]*\]\.(additionalOwnerships\[[0-9]*\]\.)?decimalInterest/gm;
        const regexExecutiveRights = /ownerships\[[0-9]*\]\.executiveRights/gm;
        const regexNetAcres = /ownerships\[[0-9]*\]\.(additionalOwnerships\[[0-9]*\]\.)?netAcres/gm;
        const regexIndex = /\[[0-9]*\]/gm;

        if (regexDecimalInterest.exec(activeField) || regexNetAcres.exec(activeField) || regexExecutiveRights.exec(activeField)) {
            const stringMatches = activeField.match(regexIndex);

            let ownership: Ownership = undefined;
            if (stringMatches.length === 1) {
                const ownershipIndex = parseInt(stringMatches[0].substring(1, stringMatches[0].length - 1), 10);
                ownership = (props.form.getState().values as AnyObject).ownerships[ownershipIndex];
            } else if (stringMatches.length === 2) {
                const ownershipIndex = parseInt(stringMatches[0].substring(1, stringMatches[0].length - 1), 10);
                const addOwnershipIndex = parseInt(stringMatches[1].substring(1, stringMatches[1].length - 1), 10);
                ownership = (props.form.getState().values as AnyObject).ownerships[ownershipIndex].additionalOwnerships[addOwnershipIndex];
            } else {
                return;
            }

            if (ownership) {
                if (!initialInterestsRef.current.find((x) => x.id === ownership.id)) {
                    setInitialInterests([
                        ...initialInterestsRef.current,
                        {
                            id: ownership.id,
                            value: ownership.decimalInterest,
                        },
                    ]);
                }

                if (!initialExecutiveRightsRef.current.find((x) => x.id === ownership.id)) {
                    setInitialExecutiveRights([
                        ...initialExecutiveRightsRef.current,
                        {
                            id: ownership.id,
                            value: ownership.executiveRights,
                        },
                    ]);
                }
            }
        }
    }, [props.form]);

    const updateDeltaInterestItem = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const activeField = props.form.getState().active;

        const regexDecimalInterest = /ownerships\[[0-9]*\]\.(additionalOwnerships\[[0-9]*\]\.)?decimalInterest/gm;
        const regexNetAcres = /ownerships\[[0-9]*\]\.(additionalOwnerships\[[0-9]*\]\.)?netAcres/gm;
        const regexExecutiveRights = /ownerships\[[0-9]*\]\.executiveRights/gm;
        const regexIndex = /\[[0-9]*\]/gm;

        if (regexDecimalInterest.exec(activeField) || regexNetAcres.exec(activeField) || regexExecutiveRights.exec(activeField)) {
            const stringMatches = activeField.match(regexIndex);

            let ownership: Ownership = undefined;
            if (stringMatches.length === 1) {
                const ownershipIndex = parseInt(stringMatches[0].substring(1, stringMatches[0].length - 1), 10);
                ownership = (props.form.getState().values as AnyObject).ownerships[ownershipIndex];
            } else if (stringMatches.length === 2) {
                const ownershipIndex = parseInt(stringMatches[0].substring(1, stringMatches[0].length - 1), 10);
                const addOwnershipIndex = parseInt(stringMatches[1].substring(1, stringMatches[1].length - 1), 10);
                ownership = (props.form.getState().values as AnyObject).ownerships[ownershipIndex].additionalOwnerships[addOwnershipIndex];
            } else {
                return;
            }

            if (ownership) {
                let existingDelta = deltaInterestsRef.current.find((x) => x.id === ownership.id);

                if (!!existingDelta) {
                    const originalOwnerInterest = initialInterestsRef.current.find((x) => x.id === ownership.id);
                    const deltaInterest = ownership.decimalInterest - parseFloat(originalOwnerInterest.value);

                    setDeltaInterests([
                        ...deltaInterestsRef.current.filter((x) => x.id !== ownership.id),
                        {
                            id: ownership.id,
                            value: deltaInterest,
                        },
                    ]);
                } else {
                    // if doesn't exist do this
                    const originalOwnerInterest = initialInterestsRef.current.find((x) => x.id === ownership.id).value;
                    const deltaInterest = ownership.decimalInterest - originalOwnerInterest;
                    setDeltaInterests([
                        ...deltaInterestsRef.current,
                        {
                            id: ownership.id,
                            value: deltaInterest,
                        },
                    ]);
                }

                if (!deltaExecutiveRightsRef.current.find((x) => x.id === ownership.id)) {
                    const originalExecutiveRights = initialExecutiveRightsRef.current.find((x) => x.id === ownership.id).value;
                    const deltaExecutiveRight = ownership.executiveRights - originalExecutiveRights;
                    setDeltaExecutiveRights([
                        ...deltaExecutiveRightsRef.current,
                        {
                            id: ownership.id,
                            value: deltaExecutiveRight,
                        },
                    ]);
                }

                if (deltaExecutiveRightsRef.current.find((x) => x.id === ownership.id)) {
                    const originalExecutiveRights = initialExecutiveRightsRef.current.find((x) => x.id === ownership.id);
                    const deltaExecutiveRight = ownership.executiveRights - parseFloat(originalExecutiveRights.value);

                    setDeltaExecutiveRights([
                        ...deltaExecutiveRightsRef.current.filter((x) => x.id !== ownership.id),
                        {
                            id: ownership.id,
                            value: deltaExecutiveRight,
                        },
                    ]);
                }
            }
        }
    }, [props.form]);

    const calcTotalInterest = () => {
        const newTotalInterest = props.totalInterest ? props.totalInterest : 0;
        const calculatedInterest = newTotalInterest + deltaInterests.reduce((acc, current) => acc + current.value, 0);
        let formattedInterest = parseFloat(MathUtil.cleanNumber(calculatedInterest));

        // need to calculate with better precision
        if (formattedInterest < 1 && interestStatus !== 'low') {
            setInterestStatus('low');
        } else if (formattedInterest > 1 && interestStatus !== 'high') {
            setInterestStatus('high');
        } else if (formattedInterest === 1 && interestStatus !== 'good') {
            setInterestStatus('good');
        }
        setTotalInterest(formattedInterest);
    };

    const calcTotalAcres = () => {
        if (!Number.isNaN(totalInterest)) {
            setTotalAcreage(MathUtil.cleanNumber(props.grossAcres ? totalInterest * props.grossAcres : 0));
        } else {
            setTotalAcreage('Please enter a valid number');
        }
    };

    const totalExecutiveRights = () => {
        const newExecutiveRights = props.executiveRights ? props.executiveRights : 0;
        const calculatedExecutiveRights = newExecutiveRights + deltaExecutiveRights.reduce((acc, current) => acc + current.value, 0);

        // need to calculate with better precision
        if (calculatedExecutiveRights < 1 && rightsStatus !== 'low') {
            setRightsStatus('low');
        } else if (calculatedExecutiveRights > 1 && rightsStatus !== 'high') {
            setRightsStatus('high');
        } else if (calculatedExecutiveRights === 1 && rightsStatus !== 'good') {
            setRightsStatus('good');
        }

        return calculatedExecutiveRights.toString();
    };

    // tslint:disable-next-line:max-line-length
    const ownershipContactCategoryItems = useMemo(() =>
        props.ownershipContactCategories && props.ownershipContactCategories.length > 0
            ? props.ownershipContactCategories
                .map((type: OwnershipContactCategory) => new SelectItem(type.id, type.name))
            : undefined,
        [props.ownershipContactCategories]);

    const leaseBuyers = useMemo(() =>
        props?.projectStaff?.items && props.projectStaff.items.length > 0
            ? props.projectStaff.items
                  .map((landman: ProjectStaff) => new SelectItem(landman.id, landman.user.firstName + ' ' + landman.user.lastName))
            : undefined,
        [props.projectStaff]);

    const incrementOwnershipPage = () => {
        let ownerConfig = {...ownershipConfig};
        ownerConfig.pageNumber += 1;
        setOwnershipConfig(ownerConfig);
    };

    const searchUpdate = _.debounce((input: string) => {
        setSearchText(input);
        let newConfig = {...ownershipConfig};
        newConfig.pageNumber = 1;
        setOwnershipConfig(newConfig);
    }, 500);

    const card = (ownership: Ownership, index: number) => {
        return (
            <div key={ownership.id}>
                <OwnershipCard
                    ownership={ownership}
                    index={index}
                    contactCategories={ownershipContactCategoryItems}
                    categoryTypes={props.project?.ownershipCategoryTypes}
                    addAdditionalOwnership={props.addAdditionalOwnership}
                    deleteAdditionalOwnerships={props.deleteAdditionalOwnership}
                    navigateLeaseInfo={onNavigateLeaseInfo}
                    openOwnershipNotes={props.openOwnershipNotes}
                    deleteOwnership={onDeleteOwnership}
                    makeMineralOwnership={onMakeMineralOwnership}
                    leaseBuyers={leaseBuyers}
                    onInterestFocus={addInitialInterestItem}
                    onInterestChange={updateDeltaInterestItem}
                    hasNonExecutiveAdditionalOwnership={props.hasNonExecutiveAdditionalOwnership}
                    navigateAcquisition={props.navigateAcquisition}
                    openContactNotes={props.openContactNotes}
                    isCommittedCategoriesHidden={props.project?.isCommittedCategoriesHidden}
                    onRatifyAdditionalOwner={onRatifyAdditionalOwner}
                />
            </div>
        );
    };

    const cardContainer = () => {
        if (!loadingItems && (!ownerships || ownerships?.totalRecords <= 0)) {
            return <div>No Results Found</div>;
        }

        const options = ODataOptions.from(ownershipConfig);

        return (
            <>
                <div className={'form-field'}>
                    <label htmlFor={`search by owner name`} style={{marginRight: '1rem'}}>
                        Search By Owner Name
                    </label>
                    <input
                        className="field"
                        style={{ width: '100%' }}
                        onChange={(e) => searchUpdate(e.target.value)}
                    />
                    <br />
                </div>
                {ownerships && (
                    <div>
                        <FormSpy onChange={(e: FormState<AnyObject>) => onFormChange(e, props.form)} subscription={{ active: true, values: true }} />
                        <PagedItemList
                            elementId="ownership-list-scroll"
                            style={{ overflowY: loadingItems ? 'hidden' : 'scroll', maxHeight: '73vh' }}
                            // tslint:disable-next-line: max-line-length
                            resultsLabelStyle={{ marginLeft: '1rem', marginRight: '2.1rem', background: 'gainsboro', border: '1px solid gainsboro', borderTopLeftRadius: '5px', borderTopRightRadius: '5px' }}
                            resetLastPage={false}
                            ResetStateCallback={null}
                            dataOptions={options}
                            GetPagedItemList={() => incrementOwnershipPage()}
                            DisplayList={() => ownerships.items?.map(card)}
                            orderByColumn={options.getOrderByProperty() !== undefined ?
                                options.getOrderByProperty() : 'Id'}
                            orderByDirection={options.getOrderByDirection() !== undefined ?
                                options.getOrderByDirection() : ODataOrderByDirection.Descending}
                            startingPage={1}
                            rowsPerPage={initConfig.rowCount}
                            totalRows={ownerships.totalRecords}
                        />
                    </div>
                )}
                {loadingItems && (
                    <>
                        <LoadingSpinner />
                        <span>Loading Ownerships...</span>
                    </>
                )}
            </>
        );
    };

    const showManageLeasehold = () => {
        props.setDialogShowing(DialogList.MANAGE_LEASEHOLD);
        props.getLeaseholdsByTract(props.clientNumber, props.projectNumber, props.fieldNumber, props.tractNumber);
    };

    const showAddOwnership = () => {
        props.initOwnerTracts();
        props.onAddOwnership();
    };

    const handleSelectedId = (e: AnyObject, field: 'Lease Buyer' | 'Contact Category') => {
        const currentForm: AnyObject = props.form.getState().values;
        const ownershipList = currentForm.ownerships;

        ownershipList.forEach((o: Ownership, index: number) => {
            if (field === 'Lease Buyer') {
                props.form.change(`ownerships[${index}].leaseBuyer`, parseInt(e.target.value + '', 10));
            } else if (field === 'Contact Category') {
                props.form.change(`ownerships[${index}].ownershipContactCategoryId`, parseInt(e.target.value + '', 10));
            }
        });
    };

    const handleRegisterOwnerships = () => {
        props.form.change('ownerships', ownerships.items);

        props.resetForm(ownerships);
    };

    const leaseBuyerDropdown = () => {
        return (
            <RHFSelect
                label="Lease Buyer"
                name=""
                options={
                    !!leaseBuyers
                        ? leaseBuyers.map((l) => {
                              return { value: l.id, label: `${l.name}` };
                          })
                        : []
                }
                onChange={(e) => handleSelectedId(e, 'Lease Buyer')}
            />
        );
    };

    const contactCategoryDropdown = () => {
        return (
            <RHFSelect
                label="Contact Category"
                name=""
                options={
                    !!props?.ownershipContactCategories
                        ? props.ownershipContactCategories.map((cc) => {
                              return { value: cc.id, label: `${cc.name}` };
                          })
                        : []
                }
                onChange={(e) => handleSelectedId(e, 'Contact Category')}
            />
        );
    };

    const onCopyOwnership = () => {
        dialogContext.leasehold.copyOwnership(
            'ownership', 
            { projectNumber: props.projectNumber, clientNumber: props.clientNumber },
            massageCloneData
        );
    };

    const hideButton = (buttonName: 'Lease Buyer' | 'Contact Category') => {
        if (buttonName === 'Lease Buyer') {
            setShowLeaseButton(false);
        } else if (buttonName === 'Contact Category') {
            setShowCategoryButton(false);
        }
    };

    const massageCloneData = (context: string, options: string) => {
        const current = `${props.clientNumber}.${props.projectNumber}.${props.fieldNumber}.${props.tractNumber}-${props.horizonNumber}`;

        const newOptions = JSON.parse(options);
        newOptions.targetHorizon = current;

        props.cloneData(context, JSON.stringify(newOptions));
    };

    return (
        <div className="ownership-list" style={{ overflow: 'auto', minHeight: '35rem' }}>
            <div className="ownership-list-heading">
                <div style={{ display: 'flex' }}>
                    {props.hasNonExecutiveAdditionalOwnership && (
                        <div style={{ margin: '.5rem', padding: '.5rem' }}>
                            <div style={{ fontSize: '1.1em', fontWeight: 'bold' }}>Total Executive Rights</div>
                            <div className={`interest-status-${rightsStatus}`} style={{ padding: '.5rem', marginTop: '.5rem' }}>
                                {parseFloat(MathUtil.cleanNumber(totalExecutiveRights(), 7))}
                            </div>
                        </div>
                    )}
                    <div style={{ margin: '.5rem', padding: '.5rem' }}>
                        <div style={{ fontSize: '1.1em', fontWeight: 'bold' }}>Total Interest</div>
                        <div className={`interest-status-${interestStatus}`} style={{ padding: '.5rem', marginTop: '.5rem' }}>
                            {!Number.isNaN(totalInterest) ? MathUtil.cleanNumber(totalInterest) : 'Please enter a valid number'}
                        </div>
                    </div>
                    <div style={{ margin: '.5rem', padding: '.5rem' }}>
                        <div style={{ fontSize: '1.1em', fontWeight: 'bold' }}>Total Acreage</div>
                        <div className={`interest-status-${interestStatus}`} style={{ padding: '.5rem', marginTop: '.5rem' }}>
                            {totalAcreage}
                        </div>
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ display: 'flex', gap: '1rem', marginRight: '1rem' }}>
                        {showCategoryButton && (
                            <Button
                                text="Set Owners' Contact Category"
                                size="medium"
                                intent="primary"
                                onClick={() => hideButton('Contact Category')}
                                disabled={props?.ownershipContactCategories?.length === 0 ? true : false}
                            />
                        )}
                        {!showCategoryButton && contactCategoryDropdown()}
                    </div>
                    <div style={{ display: 'flex', gap: '1rem', marginRight: '1rem' }}>
                        {showLeaseButton && (
                            <Button
                                text="Set Owners' Lease Buyer"
                                size="medium"
                                intent="primary"
                                onClick={() => hideButton('Lease Buyer')}
                                disabled={leaseBuyers?.length === 0 ? true : false}
                            />
                        )}
                        {!showLeaseButton && leaseBuyerDropdown()}
                    </div>
                    <Button
                        text="Copy Ownership"
                        size="medium"
                        intent="primary"
                        onClick={() => onCopyOwnership()}
                        disabled={props?.form?.getState()?.dirty}
                    />
                    <Button
                        className="ml-1"
                        text="Attach Ownership"
                        size="medium"
                        intent="primary"
                        onClick={showAddOwnership}
                        disabled={props?.form?.getState()?.dirty}
                    />
                    <div style={{ display: 'none' }}>
                        <Button
                            text="Manage Leasehold"
                            className="ml-1"
                            size="medium"
                            intent="primary"
                            onClick={showManageLeasehold}
                            disabled={props?.form?.getState()?.dirty}
                        />
                    </div>
                </div>
            </div>
            {cardContainer()}

            <LeaseholdPicker
                ownershipId={selectedOwnershipId}
                navigate={props.navigate}
                leaseholds={props.leaseholds}
                getLeaseholds={props.getLeaseholds}
                hideDialog={() => {
                    props.hideDialog();
                    props.clearVerification();
                }}
            />
        </div>
    );
};

export default OwnershipList;


// WEBPACK FOOTER //
// ./src/components/project/sections/TractComponents/OwnershipList.tsx